import { useState, useMemo } from "react";
import { Box, TableCell, TableRow, Collapse } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmailPreviewDialog from "./EmailPreviewDialog";
import { useUser } from "../../../contexts/UserContext";
import type { RFQ } from "../../../types/rfq";
import { orderedUniqueItemsOf, useRfqItemsSelection } from "./rfq-row/useRfqItemsSelection";
import { useDuplicateQuote } from "./rfq-row/useDuplicateQuote";
import RfqSummaryRow from "./rfq-row/RfqSummaryRow";
import RfqItemsPanel from "./rfq-row/RfqItemsPanel";

interface ExpandableRowProps {
  rfq: RFQ;
  onDelete: () => void;
  onSelect: (rfqId: number) => void;
  isSelected: boolean;
  onViewAttachments: (rfqId: number) => void;
  expanded: boolean;
  onToggleExpanded: (rfqId: number) => void;
  onBlacklist: (rfqId: number, clientEmail: string, clientName: string) => void;
}

/** The email dialog's view of an RFQ: unique items in order, with their tag flags. */
const toEmailDialogRfq = (rfq: RFQ): RFQ => ({
  ...rfq,
  created_at: rfq.created_at || new Date().toISOString(),
  updated_at: rfq.updated_at || new Date().toISOString(),
  rfq_id: typeof rfq.rfq_id === 'string' ? parseInt(rfq.rfq_id, 10) : rfq.rfq_id,
  items: orderedUniqueItemsOf(rfq.items).map(item => ({
    ...item,
    is_non_refundable:
      Array.isArray(item.remark_tags) && item.remark_tags.includes('Non-returnable'),
    is_limited_stock:
      Array.isArray(item.remark_tags) && item.remark_tags.includes('Limited Stock'),
  })),
});

/**
 * One RFQ in the list: its summary row and, expanded, its items (selection
 * and bulk actions: useRfqItemsSelection; duplicate-quote banner:
 * useDuplicateQuote), plus the quote email dialog.
 */
export default function ExpandableRow({ rfq, onDelete, onSelect, isSelected, onViewAttachments, expanded, onToggleExpanded, onBlacklist }: ExpandableRowProps) {
  const { selectedUser } = useUser();
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const items = useRfqItemsSelection(rfq, selectedUser);

  // Memoized: a fresh object on every render made the dialog treat each
  // parent re-render as new items and drop the generated quote PDF.
  const emailDialogRfq = useMemo(() => toEmailDialogRfq(rfq), [rfq]);

  const duplicateQuote = useDuplicateQuote(rfq, expanded, selectedUser);

  // Check if all items have validated prices and indents
  const allItemsValidated = (rfq.items || []).every(
    (item) => item.price_validated
  );

  return (
    <>
      <RfqSummaryRow
        rfq={rfq}
        expanded={expanded}
        isSelected={isSelected}
        allItemsValidated={allItemsValidated}
        onToggleExpanded={() => onToggleExpanded(rfq.rfq_id)}
        onSelect={onSelect}
        onDelete={onDelete}
        onViewAttachments={onViewAttachments}
        onBlacklist={() => onBlacklist(rfq.rfq_id, rfq.client_email, rfq.client_name)}
        onOpenEmailDialog={() => setEmailDialogOpen(true)}
      />

      {/* Expanded row content */}
      <TableRow>
        <TableCell colSpan={6} sx={(theme) => ({
          py: 0,
          px: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.025),
          transition: "all 0.2s ease-in-out",
          // Collapsed: no line (the header row has one). Expanded: a firm line
          // and the accent bar so the next RFQ clearly starts after it.
          borderBottom: expanded ? `2px solid ${alpha(theme.palette.primary.main, 0.25)}` : "none",
          boxShadow: expanded ? `inset 3px 0 0 ${theme.palette.primary.main}` : "none",
        })}>
          <Collapse
            in={expanded}
            timeout={200}
            unmountOnExit
            sx={{
              transition: "all 0.2s ease-in-out",
              opacity: expanded ? 1 : 0
            }}
          >
            <Box sx={{
              pt: 2.5,
              pb: 3,
              pl: { xs: 2, md: 3 },
              pr: { xs: 2, md: 2.5 },
              opacity: expanded ? 1 : 0,
              transform: expanded ? "translateY(0)" : "translateY(-10px)",
              transition: "all 0.2s ease-in-out"
            }}>
              <RfqItemsPanel
                rfq={rfq}
                selectedUser={selectedUser}
                allItemsValidated={allItemsValidated}
                duplicateQuote={duplicateQuote}
                items={items}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Email Dialog */}
      <EmailPreviewDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        rfq={emailDialogRfq}
      />
    </>
  );
}
